// 照片集合页 — 跨套系的生成照片总库 (2026-08-09 运营需求 #4):
// 所有生成的候选(可切 已选/上架)集中在一页, 大图挑选, 转移到合适的套系。
//
// 后端: GET /admin/v1/content/plates?app_id=&status=&page=&page_size=
//       PUT /admin/v1/content/plates/:id/reassign (candidate → 目标套系候选)
//       DELETE /admin/v1/content/plates/:id (candidate)

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Image as AntImage,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { listApps, type App } from '@/api/apps';
import { listPackages } from '@/api/content';
import { listPlatesLibrary, reassignPlate, selectCandidate, type LibraryPlate } from '@/api/plates';
import { del } from '@/api/client';

const { Text } = Typography;

const STATUS_OPTIONS = [
  { value: 'candidate', label: '候选（未选入）' },
  { value: 'draft', label: '已选入（待标框/上架）' },
  { value: 'active', label: '已上架' },
];

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  candidate: { color: 'orange', label: '候选' },
  draft: { color: 'blue', label: '已选入' },
  active: { color: 'green', label: '已上架' },
};

export function PlatesLibraryPage() {
  const [apps, setApps] = useState<App[]>([]);
  const [appId, setAppId] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<string>('candidate');
  const [rows, setRows] = useState<LibraryPlate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [pkgOptions, setPkgOptions] = useState<{ value: number; label: string }[]>([]);
  const [transferId, setTransferId] = useState<number | null>(null);
  const [transferTarget, setTransferTarget] = useState<number | undefined>(undefined);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    listApps({ page_size: 100 })
      .then((r) => {
        setApps(r.data);
        const first = r.data.find((a) => a.status === 'active') ?? r.data[0];
        if (first) setAppId((prev) => prev ?? first.id);
      })
      .catch(() => message.error('加载 App 列表失败'));
  }, []);

  const load = useCallback(() => {
    if (!appId) return;
    setLoading(true);
    listPlatesLibrary({ app_id: appId, status, page, page_size: 24 })
      .then((r) => {
        setRows(r.data);
        setTotal(r.page?.total ?? r.data.length);
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, [appId, status, page]);
  useEffect(load, [load]);

  // 服务端搜索 (q → ILIKE): App 下套系可达数千个, 只拉一页做客户端过滤搜不到
  // 排名靠后的套系 (2026-08-09 "搜索不到中秋" 根因)。
  const [pkgSearching, setPkgSearching] = useState(false);
  const pkgSearchTimer = useRef<ReturnType<typeof setTimeout>>();
  const searchPackages = useCallback((q: string) => {
    if (!appId) return;
    setPkgSearching(true);
    listPackages({ app_id: appId, q: q || undefined, page_size: 50 })
      .then((r) => setPkgOptions(r.data.map((x: any) => ({ value: x.id, label: `#${x.id} ${x.name}` }))))
      .catch(() => undefined)
      .finally(() => setPkgSearching(false));
  }, [appId]);
  useEffect(() => searchPackages(''), [searchPackages]);
  const onPkgSearch = (q: string) => {
    if (pkgSearchTimer.current) clearTimeout(pkgSearchTimer.current);
    pkgSearchTimer.current = setTimeout(() => searchPackages(q.trim()), 300);
  };

  const appOptions = useMemo(
    () => apps.map((a) => ({ value: a.id, label: `#${a.id} ${a.name} (${a.slug})` })),
    [apps]
  );

  const doTransfer = () => {
    if (!transferId || !transferTarget) return;
    setTransferring(true);
    reassignPlate(transferId, transferTarget)
      .then(() => {
        // 转移只改 package_id, 批次(run)仍属原套系 — 目标底版页按本套系批次
        // 分组看不到转入候选 (2026-08-09 "转移后在哪看" 根因), 在本页「选入」。
        message.success('已转移(仍为候选)。在本页筛该套系点「选入」即可进入其底版页标框/上架');
        setTransferId(null);
        load();
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '转移失败(仅候选态可转移)'))
      .finally(() => setTransferring(false));
  };

  const doSelect = (p: LibraryPlate) => {
    selectCandidate(p.package_id, p.plate_id)
      .then(() => {
        message.success(`已选入「${p.package_name || `套系#${p.package_id}`}」, 去其底版页标框/上架`);
        load();
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '选入失败'));
  };

  const doDelete = (id: number) => {
    del(`/admin/v1/content/plates/${id}`)
      .then(() => {
        message.success('已删除');
        load();
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '删除失败(仅候选态可删)'));
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Text strong>App</Text>
        <Select style={{ width: 260 }} value={appId} options={appOptions} onChange={(v) => { setAppId(v); setPage(1); }} showSearch optionFilterProp="label" />
        <Text strong>状态</Text>
        <Select style={{ width: 200 }} value={status} options={STATUS_OPTIONS} onChange={(v) => { setStatus(v); setPage(1); }} />
        <Button onClick={load}>刷新</Button>
      </Space>

      <Alert
        type="info"
        showIcon
        message="全部套系的生成照片集中在这里。点图看大图；「转移」把候选放进合适的套系，「选入」使其进入套系底版页（可标框/上架）。转移来的候选只在本页可见，选入后才出现在套系底版页。"
      />

      {loading ? (
        <Spin style={{ display: 'block', margin: '80px auto' }} />
      ) : rows.length === 0 ? (
        <Text type="secondary">暂无照片</Text>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          <AntImage.PreviewGroup>
            {rows.map((p) => (
              <div key={p.plate_id} style={{ width: 180, border: '1px solid #f0f0f0', borderRadius: 8, padding: 8, textAlign: 'center' }}>
                <AntImage
                  src={p.plate_image_url}
                  width="100%"
                  height={220}
                  style={{ objectFit: 'cover', borderRadius: 6 }}
                  preview={{ mask: '查看大图' }}
                />
                <div style={{ margin: '6px 0 2px', fontSize: 12 }}>
                  <Tag color={STATUS_TAG[p.status]?.color}>{STATUS_TAG[p.status]?.label ?? p.status}</Tag>
                </div>
                <Link to={`/content/${p.package_id}/plates`} style={{ fontSize: 12 }}>
                  {p.package_name || `套系#${p.package_id}`}
                </Link>
                <div style={{ marginTop: 6 }}>
                  <Space size={4}>
                    {p.status === 'candidate' && (
                      <>
                        <Button size="small" type="link" onClick={() => doSelect(p)}>
                          选入
                        </Button>
                        <Button size="small" type="link" icon={<SwapOutlined />} onClick={() => { setTransferId(p.plate_id); setTransferTarget(undefined); }}>
                          转移
                        </Button>
                        <Popconfirm title="删除该照片?" okText="删除" okButtonProps={{ danger: true }} onConfirm={() => doDelete(p.plate_id)}>
                          <Button size="small" danger type="link">删除</Button>
                        </Popconfirm>
                      </>
                    )}
                  </Space>
                </div>
              </div>
            ))}
          </AntImage.PreviewGroup>
        </div>
      )}

      <Pagination current={page} pageSize={24} total={total} onChange={setPage} showTotal={(t) => `共 ${t} 张`} />

      <Modal
        title="转移到其他套系"
        open={!!transferId}
        onOk={doTransfer}
        okText="转移"
        confirmLoading={transferring}
        okButtonProps={{ disabled: !transferTarget }}
        onCancel={() => setTransferId(null)}
        destroyOnClose
      >
        <Select
          style={{ width: '100%' }}
          showSearch
          filterOption={false}
          onSearch={onPkgSearch}
          loading={pkgSearching}
          notFoundContent={pkgSearching ? '搜索中…' : '无匹配套系'}
          placeholder="输入套系名搜索"
          options={pkgOptions}
          value={transferTarget}
          onChange={setTransferTarget}
        />
      </Modal>
    </Space>
  );
}
